import {createAction} from 'redux-actions'
import * as ActionTypes from './actionTypes'
import {addTimeZoneGroups, addUserGroups, addUsers} from '../../../entities/actions'
import {setEnabledFeatures} from '../../../features/actions'
import {setLicenseCounters, setNewUserIdSet, setUsersWithoutApprovers, setViewerManagementAssignmentsCounters, setViewerSettings} from '../common/actions'
import {
    getCurrentPageNum, getNUsersPerPage, getSorting, getUserIdsOrder, getUsersFilter, getUsersWorkAssignmentsCounters, getWordsFilter,
    getWorkAssignmentsFilter
} from './selectors'
import {createActionObject} from '../../../redux/utils'
import rpcCall from "../../../util/rpc/rpcCalls"
import StringTokenizer from "../../../util/StringTokenizer"
import {createSettingsJsonData} from './utils'
import {getNewUsersIdSet} from '../common/selectors'
import {RECORDS_LOAD_LIMIT} from '../common/constants'

export const setUserIdsOrder = createAction(ActionTypes.USER_IDS_ORDER_SET)
export const prependUserIdsOrder = (ids) => (dispatch, getState) => {
    dispatch(setUserIdsOrder([...ids, ...getUserIdsOrder(getState())]))
}
export const removeUserIdFromOrder = (id) => (dispatch, getState) => {
    dispatch(setUserIdsOrder(getUserIdsOrder(getState()).filter(userId => userId !== id)))
}

export const setLoading = createAction(ActionTypes.SET_LOADING)
export const setLoaded = createAction(ActionTypes.SET_LOADED)

function isNeedMoreLoad(NUsersPerPage, partNum) {
    return NUsersPerPage > partNum * RECORDS_LOAD_LIMIT;
}

function getPageNumByRecordsLimit(partNum, pageNum, NUsersPerPage) {
    return Math.ceil(NUsersPerPage / RECORDS_LOAD_LIMIT) * (pageNum - 1) + partNum
}

export const getUsersForWorkAssignmentTab = ({usersFilter,  partNum, tokens}) => (dispatch, getState) =>  {

    let NUsersPerPage = getNUsersPerPage(getState())

    rpcCall(
        'UserManagementService.getUsersWorkAssignmentsCounters',
        [
            usersFilter.userIds,
            usersFilter.groupIds,
            usersFilter.groupType,
            usersFilter.allSelected,
            getWorkAssignmentsFilter(getState()),
            tokens,
            getSorting(getState()),
            getPageNumByRecordsLimit(partNum, getCurrentPageNum(getState()), NUsersPerPage),
            RECORDS_LOAD_LIMIT
        ],
        () => {return false}
    )
        .then((data) => {

            dispatch(setUsersWorkAssignmentsCounters({...getUsersWorkAssignmentsCounters(getState()), ...data.map}))

            if(isNeedMoreLoad(NUsersPerPage, partNum)) {
                partNum++
                dispatch(getUsersForWorkAssignmentTab({usersFilter, partNum, tokens}))
            } else {
                dispatch(setLoading(false))
            }
        })
        .catch((err) => {
            dispatch(createActionObject(ActionTypes.LOAD_USERS_DATA_FAILURE))
            console.error(err)
        })
}

export const loadUsersDataAction = () => (dispatch, getState) => {
    dispatch(createAction(ActionTypes.LOAD_USERS_DATA)())

    const usersFilter = getUsersFilter(getState())
    const wordsFilter = getWordsFilter(getState())
    const tokens = new StringTokenizer(StringTokenizer.PUNCTUATORS_FOR_SEARCH).getTokens(wordsFilter.toLowerCase())

    dispatch(setLoaded(true))

    rpcCall('UserManagementService.getWorkAssignmentTabSettings',
        [
            usersFilter.userIds,
            usersFilter.groupIds,
            usersFilter.groupType,
            usersFilter.allSelected,
            getWorkAssignmentsFilter(getState()),
            tokens,
            getSorting(getState()),
            getCurrentPageNum(getState()),
            getNUsersPerPage(getState()),
        ],
        () => {return false}
    )
        .then(
            (data) => {

                dispatch(createActionObject(ActionTypes.LOAD_USERS_DATA_SUCCESS, {
                    nTotalUsers: data.totalRecords,
                    currentPage: data.pageNum,
                }))

                if (data.allUserGroups !== null) {
                    dispatch(addUserGroups(data.allUserGroups))
                }
                if (data.allTimeZoneGroups !== null) {
                    dispatch(addTimeZoneGroups(data.allTimeZoneGroups))
                }

                dispatch(createActionObject(ActionTypes.SET_SUBMITTED_WORDS_FILTER, wordsFilter))
                dispatch(setEnabledFeatures(data.viewerSettings.enabledFeatures))
                dispatch(setViewerSettings(data.viewerSettings))
                dispatch(setLicenseCounters(data.licenseCounters))
                dispatch(setViewerManagementAssignmentsCounters(data.viewerManagementAssignmentsCounters))
                dispatch(setUsersWithoutApprovers(data.usersWithoutApprovers))

                const newUserIdsSet = getNewUsersIdSet(getState())

                const userIds = [
                    ...Object.keys(newUserIdsSet),
                    ...data.records.filter(record => !newUserIdsSet[record.id]).map(record => record.id)
                ]
                dispatch(addUsers(data.records))
                dispatch(setUserIdsOrder(userIds))

                dispatch(setLoading(true))

                dispatch(getUsersForWorkAssignmentTab({usersFilter, partNum: 1, tokens}))
            }
        )
        .catch((err) => {
            dispatch(createActionObject(ActionTypes.LOAD_USERS_DATA_FAILURE))
            console.error(err)
        })
}

export const saveUserSettings = () => (dispatch, getState) => {
    rpcCall(
        'UserManagementService.saveUserSettingsForWorkAssignmentsTab',
        [
            JSON.stringify(createSettingsJsonData(
                getUsersFilter(getState()),
                getSorting(getState()),
                getWorkAssignmentsFilter(getState()),
                getNUsersPerPage(getState()),
            ))
        ],
        () => false
    ).then(() => {}).catch(console.error)
}

export const changeSorting = (sorting) => dispatch => {
    dispatch(createActionObject(ActionTypes.SET_SORTING, sorting))
    dispatch(reloadData())
    dispatch(saveUserSettings())
}

export const changeUsersFilter = (usersFilter) => dispatch => {
    dispatch(createActionObject(ActionTypes.SET_USERS_FILTER, usersFilter))
    dispatch(reloadData())
    dispatch(saveUserSettings())
}

export const changeWordsFilter = (text) => dispatch => {
    dispatch(createActionObject(ActionTypes.SET_WORDS_FILTER, text))
    dispatch(reloadData())
}

export const changeUsersPerPage = (usersPerPage) => dispatch => {
    dispatch(createActionObject(ActionTypes.SET_USERS_PER_PAGE, usersPerPage))
    dispatch(reloadData())
    dispatch(saveUserSettings())
}

export const changeCurrentPageNum = (pageNum) => dispatch => {
    dispatch(createActionObject(ActionTypes.SET_PAGE_NUM, pageNum))
    dispatch(reloadData())
}

export const changeWorkAssignmentsFilter = (filter) => dispatch => {
    dispatch(createActionObject(ActionTypes.SET_WORK_ASSIGNMENTS_FILTER, filter))
    dispatch(reloadData())
    dispatch(saveUserSettings())
}

const reloadData = () => dispatch => {
    dispatch(setNewUserIdSet({}))
    dispatch(setLoaded(false))
}

export const setUsersWorkAssignmentsCounters = createAction(ActionTypes.SET_USERS_WORK_ASSIGNMENTS_COUNTERS)
export const setWorkAssignmentsCountersForUser = (userId, counters) => dispatch => {
    dispatch(createActionObject(
        ActionTypes.ADD_USER_WORK_ASSIGNMENTS_COUNTERS,
        {
            [userId]: counters
        }
    ))
}



// WEBPACK FOOTER //
// ./web/js/administration/userManagement/workAssignmentsTab/actions.js